import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Bookmark, BookmarkCheck, Flag, CalendarClock, ArrowRight, SlidersHorizontal, Scale, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { scholarships } from "@/data/scholarships";
import { computeMatch, daysUntil } from "@/lib/calc";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge, EmptyState, SectionTitle } from "@/components/common";
import { CompareModal } from "@/components/CompareModal";

const countryOpts = ["Semua", "United Kingdom", "Australia", "Eropa", "Jepang", "Berbagai Negara"];
const fundingOpts = ["Semua", "Fully Funded"];
const sortOpts = [{ v: "match", l: "Match tertinggi" }, { v: "deadline", l: "Deadline terdekat" }, { v: "name", l: "Nama A-Z" }];

export default function ScholarshipExplorer() {
  const { data, updateData } = useApp();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("Semua");
  const [funding, setFunding] = useState("Semua");
  const [sort, setSort] = useState("match");
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const toggleCompare = (id) => {
    setCompare((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) { toast.message("Maksimal 3 beasiswa untuk dibandingkan."); return prev; }
      return [...prev, id];
    });
  };

  const list = useMemo(() => {
    let arr = scholarships.map((s) => ({ s, m: computeMatch(data.profile, s), d: daysUntil(s.deadline) }));
    if (q) arr = arr.filter(({ s }) => (s.name + s.provider + s.country).toLowerCase().includes(q.toLowerCase()));
    if (country !== "Semua") arr = arr.filter(({ s }) => s.country === country);
    if (funding !== "Semua") arr = arr.filter(({ s }) => s.funding === funding);
    arr.sort((a, b) => {
      if (sort === "match") return b.m.matchScore - a.m.matchScore;
      if (sort === "name") return a.s.name.localeCompare(b.s.name);
      return (a.d ?? 99999) - (b.d ?? 99999);
    });
    return arr;
  }, [q, country, funding, sort, data.profile]);

  const toggleSave = (id) => {
    updateData((prev) => {
      const saved = prev.savedScholarships.includes(id);
      toast[saved ? "message" : "success"](saved ? "Dihapus dari tersimpan" : "Beasiswa disimpan");
      return { ...prev, savedScholarships: saved ? prev.savedScholarships.filter((x) => x !== id) : [...prev.savedScholarships, id] };
    });
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Temukan Beasiswa yang Cocok Untukmu" desc="Temukan peluang yang cocok untuk profilmu." />

      {/* Filters */}
      <div className="rounded-2xl border border-purple-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-xl border border-purple-100 bg-[#FBFAFF] px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input data-testid="scholarship-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama beasiswa / penyelenggara / negara"
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={country} onChange={setCountry} options={countryOpts} testid="filter-country" icon={Flag} />
            <Select value={funding} onChange={setFunding} options={fundingOpts} testid="filter-funding" />
            <Select value={sort} onChange={setSort} options={sortOpts.map((o) => o.l)} display={sortOpts} testid="filter-sort" icon={SlidersHorizontal}
              raw onRaw={(l) => setSort(sortOpts.find((o) => o.l === l).v)} current={sortOpts.find((o) => o.v === sort).l} />
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState testid="scholarship-empty" title="Belum ada beasiswa yang cocok" desc="Coba ubah filter atau lengkapi profilmu untuk matching yang lebih relevan." />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map(({ s, m, d }) => {
            const saved = data.savedScholarships.includes(s.id);
            const inCompare = compare.includes(s.id);
            return (
              <div key={s.id} data-testid={`scholarship-card-${s.id}`} className="flex flex-col rounded-2xl border border-purple-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:shadow-purple-900/5">
                <div className="flex items-start justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-[#7C3AED]"><Flag className="h-5 w-5" /></span>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] px-2.5 py-0.5 text-xs font-bold text-white">{m.matchScore}% Match</span>
                    <button onClick={() => toggleSave(s.id)} data-testid={`save-${s.id}`} className="text-[#7C3AED]">
                      {saved ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <h3 className="mt-3 font-display text-base font-bold text-slate-900">{s.name}</h3>
                <p className="text-xs text-slate-400">{s.provider}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-[#6D28D9]">{s.country}</span>
                  <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-[#6D28D9]">{s.degree}</span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">{s.funding}</span>
                </div>
                <p className="mt-3 flex items-center gap-1 text-xs text-slate-500"><CalendarClock className="h-3.5 w-3.5" /> {s.deadline ? `Deadline: ${s.deadline}${d > 0 ? ` (${d} hari)` : " (lewat)"}` : "Deadline: belum diverifikasi"}</p>
                <div className="mt-2"><StatusBadge status={s.status} /></div>
                <div className="mt-auto space-y-2 pt-4">
                  <button onClick={() => toggleCompare(s.id)} data-testid={`compare-${s.id}`}
                    className={`flex w-full items-center justify-center gap-1.5 rounded-full border py-1.5 text-xs font-semibold transition-all ${inCompare ? "border-[#7C3AED] bg-purple-50 text-[#5B21B6]" : "border-purple-100 text-slate-500 hover:bg-purple-50"}`}>
                    <Scale className="h-3.5 w-3.5" /> {inCompare ? "Ditambahkan" : "Bandingkan"}
                  </button>
                  <Link to={`/app/beasiswa/${s.id}`}><Button variant="outline" className="w-full rounded-full border-purple-200 text-[#5B21B6] hover:bg-purple-50" data-testid={`view-${s.id}`}>Lihat Detail <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-center text-xs text-slate-400">Skor match adalah diagnostik kecocokan profil, <b>bukan</b> probabilitas penerimaan.</p>

      {compare.length > 0 && (
        <div className="fixed inset-x-0 bottom-16 z-30 px-4 lg:bottom-4 lg:left-64">
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 rounded-2xl border border-purple-200 bg-white/95 p-3 shadow-xl backdrop-blur">
            <div className="flex items-center gap-2 overflow-x-auto">
              <span className="shrink-0 text-sm font-semibold text-slate-700">Bandingkan ({compare.length}/3):</span>
              {compare.map((id) => {
                const sc = scholarships.find((x) => x.id === id);
                return (
                  <span key={id} className="flex shrink-0 items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-xs font-medium text-[#6D28D9]">
                    {sc.name.split(" ")[0]}
                    <button onClick={() => toggleCompare(id)} className="text-[#7C3AED]"><X className="h-3 w-3" /></button>
                  </span>
                );
              })}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCompare([])} className="text-slate-400">Hapus</Button>
              <Button size="sm" onClick={() => setShowCompare(true)} disabled={compare.length < 2} data-testid="open-compare" className="rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]">Bandingkan</Button>
            </div>
          </div>
        </div>
      )}

      {showCompare && <CompareModal ids={compare} profile={data.profile} onClose={() => setShowCompare(false)} />}
    </div>
  );
}

function Select({ value, onChange, options, display, testid, icon: Icon, raw, onRaw, current }) {
  return (
    <div className="relative inline-flex items-center gap-1.5 rounded-xl border border-purple-100 bg-white px-3 py-2 text-sm">
      {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      <select data-testid={testid} value={raw ? current : value} onChange={(e) => (raw ? onRaw(e.target.value) : onChange(e.target.value))}
        className="cursor-pointer bg-transparent pr-1 font-medium text-slate-600 outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
